import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import {
  FcGenderTypeDataDto,
  FcInsuranceContactListItemDto,
  FcInsurancePayerListItemDto,
  FcInsurancePlanDataDto,
  FcModifyInsuranceContactListItemDto,
  FindControlAdditionalSearchParametersDto,
  FindControlAdditionalSearchParametersType,
  FormGroupValidationRules,
} from 'webcommon/shared';
import { FcGenericItemModalBaseComponent } from '../../generic/fc-generic-item-modal-base.component';
import { FcListValidationService } from '../../generic/fc-list-validation/fc-list-validation.service';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-insurance-contact-list-modal',
  templateUrl: './fc-insurance-contact-list-modal.component.html',
})
export class FcInsuranceContactListModalComponent extends FcGenericItemModalBaseComponent implements OnInit {
  public override baseId = 'fc-insurance-contact-item';
  public selectedGenderPks: string[] = [];
  public selectedInsurancePayerPks: string[] = [];
  public selectedInsurancePlanInsurancePayerUid: string | null = null;
  public selectedInsurancePlanPks: string[] = [];
  public override isSubmitted = false;

  // List Item with default value
  public listItem: FcInsuranceContactListItemDto = {
    Id: '',
    Name: '',
    ExternalID: '',
    InsurancePayer: '',
    CorporationUid: '',
    Title: '',
    InsurancePlan: '',
    InsurancePlanUid: '',

    FirstName: '',
    MiddleName: '',
    LastName: '',
    Suffix: '',
    AkaFirstName: '',
    AkaLastName: '',
    Gender: '',
    GenderUid: '',

    Phone1: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: '',
    },
    Phone2: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: '',
    },
    Email1: '',
    Email2: '',
    Notes: '',
    Inactive: false,
  };

  public insurancePlanAdditonalSearchParameters: FindControlAdditionalSearchParametersDto = {
    Type: FindControlAdditionalSearchParametersType.Standard,
    AdditionalSearchParameters: [],
  };

  public listItemDefaultValidation: FormGroupValidationRules = {
    ExternalID: {
      IsRequired: false,
      MaxLength: 20,
    },
    CorporationUid: {
      IsRequired: true,
    },
    Title: {
      IsRequired: false,
      MaxLength: 50,
    },
    InsurancePlanUid: {
      IsRequired: false,
    },
    FirstName: {
      IsRequired: true,
      MaxLength: 50,
    },
    MiddleName: {
      IsRequired: false,
      MaxLength: 50,
    },
    LastName: {
      IsRequired: true,
      MaxLength: 50,
    },
    Suffix: {
      IsRequired: false,
      MaxLength: 50,
    },
    AkaFirstName: {
      IsRequired: false,
      MaxLength: 50,
    },
    GenderUid: {
      IsRequired: false,
    },
    AkaLastName: {
      IsRequired: false,
      MaxLength: 50,
    },
    Email: {
      IsRequired: false,
      MaxLength: 100,
    },
    Notes: {
      IsRequired: false,
      MaxLength: 65535,
    },
  };

  // Form Controls
  public externalIdControl: FormControl = new FormControl('');
  public insurancePayerControl: FormControl = new FormControl('');
  public corporationUidControl: FormControl = new FormControl('');
  public titleControl: FormControl = new FormControl('');
  public insurancePlanControl: FormControl = new FormControl('');
  public insurancePlanUidControl: FormControl = new FormControl('');
  public firstNameControl: FormControl = new FormControl('');
  public middleNameControl: FormControl = new FormControl('');
  public lastNameControl: FormControl = new FormControl('');
  public suffixControl: FormControl = new FormControl('');
  public akaFirstNameControl: FormControl = new FormControl('');
  public akaLastNameControl: FormControl = new FormControl('');
  public genderControl: FormControl = new FormControl('');
  public genderUidControl: FormControl = new FormControl('');
  public emailOneControl: FormControl = new FormControl('');
  public emailTwoControl: FormControl = new FormControl('');
  public notesControl: FormControl = new FormControl('');
  public inactiveControl: FormControl = new FormControl(false);

  // Form Group
  public phoneOneGroup: FormGroup = new FormGroup({});
  public phoneTwoGroup: FormGroup = new FormGroup({});

  public form: FormGroup = new FormGroup({
    externalId: this.externalIdControl,
    insurancePayer: this.insurancePayerControl,
    corporationUid: this.corporationUidControl,
    title: this.titleControl,
    insurancePlan: this.insurancePlanControl,
    insurancePlanUid: this.insurancePlanUidControl,
    inactive: this.inactiveControl,
    firstName: this.firstNameControl,
    middleName: this.middleNameControl,
    lastName: this.lastNameControl,
    suffix: this.suffixControl,
    akaFirstName: this.akaFirstNameControl,
    akaLastName: this.akaLastNameControl,
    gender: this.genderControl,
    genderUid: this.genderUidControl,
    phoneOne: this.phoneOneGroup,
    phoneTwo: this.phoneTwoGroup,
    emailOne: this.emailOneControl,
    emailTwo: this.emailTwoControl,
    notes: this.notesControl,
  });

  override ngOnInit() {
    super.ngOnInit();

    // Load List Item and set default validation rules
    this.loadListItem(this.listItemDefaultValidation);
  }

  public setForm(): void {
    // External ID Control
    this.externalIdControl.setValidators(this.fcListValidation.getValidators('ExternalID'));
    this.externalIdControl.setValue(this.listItem.ExternalID);

    // Title Contact Control
    this.titleControl.setValidators(this.fcListValidation.getValidators('Title'));
    this.titleControl.setValue(this.listItem.Title);

    // First Name Control
    this.firstNameControl.setValidators(this.fcListValidation.getValidators('FirstName'));
    this.firstNameControl.setValue(this.listItem.FirstName);

    // Middle Name Control
    this.middleNameControl.setValidators(this.fcListValidation.getValidators('MiddleName'));
    this.middleNameControl.setValue(this.listItem.MiddleName);

    // Last Name Control
    this.lastNameControl.setValidators(this.fcListValidation.getValidators('LastName'));
    this.lastNameControl.setValue(this.listItem.LastName);

    // Suffix Control
    this.suffixControl.setValidators(this.fcListValidation.getValidators('Suffix'));
    this.suffixControl.setValue(this.listItem.Suffix);

    // AKA First Name Control
    this.akaFirstNameControl.setValidators(this.fcListValidation.getValidators('AkaFirstName'));
    this.akaFirstNameControl.setValue(this.listItem.AkaFirstName);

    // AKA Last Name Control
    this.akaLastNameControl.setValidators(this.fcListValidation.getValidators('AkaLastName'));
    this.akaLastNameControl.setValue(this.listItem.AkaLastName);

    // Email One Control
    this.emailOneControl.setValidators(this.fcListValidation.getValidators('Email1'));
    this.emailOneControl.setValue(this.listItem.Email1);

    // Email Two Control
    this.emailTwoControl.setValidators(this.fcListValidation.getValidators('Email2'));
    this.emailTwoControl.setValue(this.listItem.Email2);

    // Notes Control
    this.notesControl.setValidators(this.fcListValidation.getValidators('Notes'));
    this.notesControl.setValue(this.listItem.Notes);

    // Inactive Control
    this.inactiveControl.setValue(this.listItem.Inactive);

    this.validateForm();
    this.populateGenderFindControl();
    this.populateInsurancePayerFindControl();
    this.populateInsurancePlanFindControl();
  }

  public getFindControlValidation(controlName: string): boolean {
    if (this.fcListValidation.validations[controlName]) {
      return !!this.fcListValidation.validations[controlName].IsRequired;
    } else {
      return !!this.listItemDefaultValidation[controlName].IsRequired;
    }
  }

  private populateGenderFindControl(): void {
    if (this.listItem.GenderUid != null && this.listItem.GenderUid.length > 0) {
      this.selectedGenderPks = [this.listItem.GenderUid];
    } else {
      this.selectedGenderPks = [];
    }
  }

  private populateInsurancePayerFindControl(): void {
    if (this.listItem.CorporationUid != null && this.listItem.CorporationUid.length > 0) {
      this.selectedInsurancePayerPks = [this.listItem.CorporationUid];
    } else {
      this.selectedInsurancePayerPks = [];
    }
  }

  private populateInsurancePlanFindControl(): void {
    if (this.listItem.InsurancePlanUid != null && this.listItem.InsurancePlanUid.length > 0) {
      this.selectedInsurancePlanPks = [this.listItem.InsurancePlanUid];
    } else {
      this.selectedInsurancePlanPks = [];
    }
  }

  public onGenderDataPopulate(genderArray: FcGenderTypeDataDto[]): void {
    const genderData: FcGenderTypeDataDto = { ...genderArray[0] };
    if (genderData.Name) {
      this.genderControl.setValue(genderData.Name);
      this.genderUidControl.setValue(genderData.GenderUid);
    } else {
      this.genderControl.setValue(this.listItem.Gender);
      this.genderUidControl.setValue(this.listItem.GenderUid);
    }
  }

  public onInsurancePayerDataPopulate(insurancePayerArray: FcInsurancePayerListItemDto[]): void {
    const insurancePayerData: FcInsurancePayerListItemDto = { ...insurancePayerArray[0] };
    const AdditionalSearchParameters = [];
    if (insurancePayerData.Name) {
      this.insurancePayerControl.setValue(insurancePayerData.Name);
      this.corporationUidControl.setValue(insurancePayerData.CorporationUid);
      AdditionalSearchParameters.push({
        ColumnName: 'InsuranceCarrierUid',
        Value: insurancePayerData.CorporationUid,
      });
    } else {
      this.insurancePayerControl.setValue(this.listItem.InsurancePayer);
      this.corporationUidControl.setValue(this.listItem.CorporationUid);
    }
    if (insurancePayerData.CorporationUid !== this.selectedInsurancePlanInsurancePayerUid) {
      this.selectedInsurancePlanPks = [];
    }
    this.insurancePlanAdditonalSearchParameters = {
      Type: FindControlAdditionalSearchParametersType.Standard,
      AdditionalSearchParameters,
    };
  }

  public onInsurancePlanDataPopulate(insurancePlanArray: FcInsurancePlanDataDto[]): void {
    const insurancePlanData: FcInsurancePlanDataDto = { ...insurancePlanArray[0] };
    this.selectedInsurancePlanInsurancePayerUid = insurancePlanData?.InsuranceCarrierUid ?? null;
    if (insurancePlanData.PayerPlanName) {
      this.insurancePlanControl.setValue(insurancePlanData.PayerPlanName);
      this.insurancePlanUidControl.setValue(insurancePlanData.InsurancePlanUid);
      if (this.insurancePayerControl.value !== insurancePlanData.InsuranceCarrierUid) {
        this.insurancePayerControl.setValue(this.listItem.InsurancePayer);
        this.corporationUidControl.setValue(this.listItem.CorporationUid);
        this.selectedInsurancePayerPks = [insurancePlanData.InsuranceCarrierUid];
      }
    } else {
      this.insurancePlanControl.setValue(this.listItem.InsurancePlan);
      this.insurancePlanUidControl.setValue(this.listItem.InsurancePlanUid);
    }
  }

  public override save(): void {
    this.isSubmitted = true;
    const values = this.form.getRawValue();

    const modifyItemDto: FcModifyInsuranceContactListItemDto = {
      ExternalID: values.externalId,
      Title: values.title,
      FirstName: values.firstName,
      MiddleName: values.middleName,
      LastName: values.lastName,
      Suffix: values.suffix,
      AkaFirstName: values.akaFirstName,
      AkaLastName: values.akaLastName,
      Email1: values.emailOne,
      Email2: values.emailTwo,
      Notes: values.notes,
      Inactive: values.inactive,
    };

    if (values.insurancePlan && values.insurancePlanUid) {
      modifyItemDto.InsurancePlanUid = values.insurancePlanUid;
    }

    if (values.insurancePayer && values.corporationUid) {
      modifyItemDto.CorporationUid = values.corporationUid;
    }

    if (values.gender && values.genderUid) {
      modifyItemDto.GenderUid = values.genderUid;
    }

    if (values.phoneOne.phoneNumber && values.phoneOne.phoneType && values.phoneOne.phoneTypeUid) {
      modifyItemDto.Phone1 = {
        PhoneNumber: values.phoneOne.phoneNumber,
        PhoneType: values.phoneOne.phoneType,
        PhoneTypeUid: values.phoneOne.phoneTypeUid,
      };
    }
    if (values.phoneTwo.phoneNumber && values.phoneTwo.phoneType && values.phoneTwo.phoneTypeUid) {
      modifyItemDto.Phone2 = {
        PhoneNumber: values.phoneTwo.phoneNumber,
        PhoneType: values.phoneTwo.phoneType,
        PhoneTypeUid: values.phoneTwo.phoneTypeUid,
      };
    }
    super.save<FcModifyInsuranceContactListItemDto>(modifyItemDto);
  }
}
